import { createContext, useContext, useEffect, useState, type DependencyList, type ReactNode } from 'react';

interface PageHeader {
  title: ReactNode;
  subtitle?: ReactNode;
}

interface PageHeaderContextType {
  header: PageHeader | null;
  setHeader: (header: PageHeader | null) => void;
}

const PageHeaderContext = createContext<PageHeaderContextType>({
  header: null,
  setHeader: () => {},
});

export const usePageHeaderValue = () => useContext(PageHeaderContext);

export const PageHeaderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [header, setHeader] = useState<PageHeader | null>(null);
  return (
    <PageHeaderContext.Provider value={{ header, setHeader }}>
      {children}
    </PageHeaderContext.Provider>
  );
};

/**
 * Cada página llama esto para que su título/subtítulo se muestre fusionado en el navbar
 * (donde antes estaba el buscador), en vez de repetirlo dentro de su propio contenido.
 * Se limpia al desmontar para que la página siguiente no herede un título viejo por un
 * instante mientras carga el suyo.
 *
 * `deps` funciona igual que en useEffect/useMemo: por defecto ([]) el título se fija una
 * sola vez al montar. Cuando el título es JSX (no un string), cada render crea un objeto
 * nuevo aunque se vea igual — si eso se pusiera como dependencia, el efecto se dispararía
 * en cada render y entraría en loop infinito con el re-render que provoca el propio
 * setHeader(). Para títulos que sí cambian con el tiempo (p. ej. AdminHistory, que pasa de
 * "Cargando..." al nombre real), hay que pasar explícitamente de qué depende ese cambio.
 */
export function usePageHeader(title: ReactNode, subtitle?: ReactNode, deps: DependencyList = []) {
  const { setHeader } = useContext(PageHeaderContext);
  useEffect(() => {
    setHeader({ title, subtitle });
    return () => setHeader(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
